import {
  buildMyStoreKeyVaultUrl,
  PROFIT_LOSS_TILE_ENABLED_SETTING_KEY,
} from "config/constants";

const PROFIT_LOSS_TILE_ENABLED_STORAGE_KEY = "profitLossTileEnabled";

const getCookie = (name: string): string | null => {
  try {
    const cookies = document.cookie?.split(";") ?? [];
    for (const cookie of cookies) {
      const [key, ...rest] = cookie.trim().split("=");
      if (key === name) return decodeURIComponent(rest.join("="));
    }
  } catch {
    // ignore cookie parsing errors
  }
  return null;
};

const getStoredValue = (name: string): string | null => {
  try {
    if (typeof sessionStorage !== "undefined") {
      const sessionValue = sessionStorage.getItem(name);
      if (sessionValue) return sessionValue;
    }
    if (typeof localStorage !== "undefined") {
      const localValue = localStorage.getItem(name);
      if (localValue) return localValue;
    }
  } catch {
    // ignore storage access errors
  }
  return getCookie(name);
};

const setStoredValue = (name: string, value: string): void => {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(name, value);
    }
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(name, value);
    }
  } catch {
    // ignore storage access errors
  }
};

const extractSettingValue = (payload: unknown): unknown => {
  if (
    typeof payload === "string" ||
    typeof payload === "boolean" ||
    typeof payload === "number" ||
    payload === null
  ) {
    return payload;
  }

  if (!payload || typeof payload !== "object") return null;

  const record = payload as Record<string, unknown>;
  if ("value" in record) return record["value"];

  const result = record["result"];
  if (result && typeof result === "object" && "value" in (result as Record<string, unknown>)) {
    return (result as Record<string, unknown>)["value"];
  }

  return null;
};

const parseBooleanSetting = (value: unknown): boolean | null => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value !== "string") return null;

  const normalized = value.trim().toLowerCase();
  if (["true", "1", "yes", "enabled", "on"].includes(normalized)) return true;
  if (["false", "0", "no", "disabled", "off"].includes(normalized)) return false;
  return null;
};

export const getStoredProfitLossTileEnabled = (): boolean | null => {
  const value = getStoredValue(PROFIT_LOSS_TILE_ENABLED_STORAGE_KEY);
  return parseBooleanSetting(value);
};

export const fetchKeyVaultSetting = async (
  key: string,
  authTokenOverride?: string
): Promise<unknown | null> => {
  try {
    const authToken = authTokenOverride || getStoredValue("authToken");
    const response = await fetch(buildMyStoreKeyVaultUrl(key), {
      method: "GET",
      headers: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });

    if (!response.ok) return null;

    const data = (await response.json()) as unknown;
    return extractSettingValue(data);
  } catch (error) {
    console.error("Error fetching Key Vault setting:", error);
    return null;
  }
};

export const fetchProfitLossTileEnabled = async (authTokenOverride?: string): Promise<boolean> => {
  const value = await fetchKeyVaultSetting(
    PROFIT_LOSS_TILE_ENABLED_SETTING_KEY,
    authTokenOverride
  );
  const parsed = parseBooleanSetting(value);
  const isEnabled = parsed === null ? true : parsed;
  setStoredValue(PROFIT_LOSS_TILE_ENABLED_STORAGE_KEY, String(isEnabled));
  return isEnabled;
};
